
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/components/LanguageContext";

export const useStudentAuthDialog = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const { toast } = useToast();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    country: "",
    city: "",
    termsAccepted: false
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Tentando fazer login com:", formData.email);
      const { data: { session }, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) {
        console.log("Erro no login:", signInError);
        toast({
          title: "Erro no login",
          description: "Email ou password incorretos.",
          variant: "destructive",
        });
        return;
      }

      if (session) {
        // Redirect based on current language
        const dashboardPath = language === 'pt' ? "/pt/student-dashboard" : "/student-dashboard";
        window.location.href = dashboardPath;
      }
    } catch (error) {
      console.error('Error during login:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.termsAccepted) {
        toast({
          title: "Erro no registo",
          description: "Você deve concordar com os termos e condições.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      console.log("Criando nova conta de estudante...");
      
      // O trigger da base de dados vai automaticamente criar o perfil e registo de estudante
      // NÃO criamos registos manuais aqui - deixamos que o trigger handle_new_user() faça o trabalho
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            is_student: true,
            first_name: formData.first_name,
            last_name: formData.last_name,
            country: formData.country,
            city: formData.city
          }
        }
      });

      if (signUpError) {
        console.log("Erro ao criar conta:", signUpError);
        if (signUpError.message.includes("User already registered")) {
          toast({
            title: "Email já registado",
            description: "Este email já está registado. Por favor faça login.",
            variant: "destructive",
          });
          setIsLogin(true);
        } else {
          toast({
            title: "Erro no registo",
            description: "Ocorreu um erro ao criar a conta.",
            variant: "destructive",
          });
        }
        return;
      }

      if (authData.user) {
        console.log("Conta criada com sucesso:", authData.user.id);
        
        // Redirect based on current language
        const dashboardPath = language === 'pt' ? "/pt/student-dashboard" : "/student-dashboard";
        window.location.href = dashboardPath;
      }
    } catch (error) {
      console.error('Error during signup:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
  };

  return {
    isSubmitting,
    isLogin,
    formData,
    setFormData,
    handleLogin,
    handleSignup,
    toggleForm
  };
};
